import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { toast } from "sonner";
import { Trash2, X, Layers, Plus, Beaker, ArrowUp, ArrowDown, Upload, FileText, ArrowRightLeft } from "lucide-react";
import TransferDialog from "@/pages/stockpiles/TransferDialog";

const COMMON_TAGS = ["clean", "polluted", "contaminated", "regulated", "hazardous", "urban_fill", "virgin_material"];
const CLASSIF_STYLE_MAP = {
  clean: "bg-emerald-50 text-emerald-800 border-emerald-200",
  virgin_material: "bg-emerald-50 text-emerald-800 border-emerald-200",
  regulated: "bg-amber-50 text-amber-800 border-amber-200",
  urban_fill: "bg-amber-50 text-amber-800 border-amber-200",
  polluted: "bg-orange-50 text-orange-800 border-orange-200",
  contaminated: "bg-red-50 text-red-800 border-red-200",
  hazardous: "bg-red-50 text-red-800 border-red-200",
};
function classifPill(tag) {
  return CLASSIF_STYLE_MAP[tag] || "bg-gray-100 text-gray-800 border-gray-300";
}

export default function Stockpiles() {
  const [piles, setPiles] = useState([]);
  const [sites, setSites] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [moveFor, setMoveFor] = useState(null);
  const [labFor, setLabFor] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [transferFor, setTransferFor] = useState(null);

  const refresh = useCallback(async () => {
    const [p, s, m] = await Promise.all([api.get("/stockpiles"), api.get("/sites"), api.get("/material-types")]);
    setPiles(p.data); setSites(s.data); setMaterialTypes(m.data);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    if (!window.confirm("Delete this stockpile?")) return;
    await api.delete(`/stockpiles/${id}`);
    refresh();
  };
  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";

  return (
    <div>
      <PageHeader
        title="Stockpiles"
        subtitle="Bin inventory"
        actions={
          <button onClick={() => setNewOpen(true)} data-testid="new-stockpile-button" className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5">
            + New Bin
          </button>
        }
      />
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stockpiles-list">
          {piles.length === 0 ? (
            <div className="col-span-full text-center text-sm text-gray-500 py-12 bg-white border border-gray-200">
              <Layers className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              No stockpiles yet. Create bins to track inventory at disposal sites.
            </div>
          ) : piles.map((p) => {
            const pct = p.capacity_tons > 0 ? Math.min(100, (p.current_tons / p.capacity_tons) * 100) : 0;
            const over = p.current_tons > p.capacity_tons;
            const highWater = pct >= 90;
            return (
              <div key={p.id} className="bg-white border border-gray-200 p-5" data-testid={`stockpile-card-${p.bin_number}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-mono text-xs text-gray-500">Bin {p.bin_number}</div>
                    <div className="font-display font-bold text-gray-900 text-lg leading-tight">{p.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{siteName(p.site_id)}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${classifPill(p.classification)}`}>{p.classification}</span>
                </div>

                <div className="border-t border-gray-100 pt-3 mt-3">
                  <div className="flex items-end justify-between mb-1">
                    <div>
                      <div className="label-caps">Current Balance</div>
                      <div className="stat-value text-2xl text-gray-900">{p.current_tons.toFixed(1)}<span className="text-gray-500 text-sm font-normal ml-1">t</span></div>
                    </div>
                    <div className="text-right font-mono text-xs text-gray-500">
                      / {p.capacity_tons.toFixed(0)} t
                    </div>
                  </div>
                  <div className="w-full h-2 bg-gray-100 relative">
                    <div className={`absolute inset-y-0 left-0 ${over ? "bg-red-500" : highWater ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  <div className="flex justify-between mt-1 text-[10px] uppercase tracking-widest font-bold text-gray-500">
                    <span>{pct.toFixed(0)}%</span>
                    {over && <span className="text-red-700">OVER CAPACITY</span>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs font-mono">
                    <div className="text-gray-500 flex items-center gap-1"><ArrowUp className="w-3 h-3 text-emerald-600" /> In</div>
                    <div className="text-gray-900 font-bold">{(p.cumulative_in_tons || 0).toFixed(1)}t</div>
                  </div>
                  <div className="text-xs font-mono">
                    <div className="text-gray-500 flex items-center gap-1"><ArrowDown className="w-3 h-3 text-amber-600" /> Out</div>
                    <div className="text-gray-900 font-bold">{(p.cumulative_out_tons || 0).toFixed(1)}t</div>
                  </div>
                </div>

                {(p.lab_reports || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="label-caps mb-1 flex items-center gap-1"><Beaker className="w-3 h-3" /> Lab: {p.lab_reports.length}</div>
                    <div className="text-xs font-mono text-gray-600">Latest: {p.lab_reports[p.lab_reports.length - 1].result} · {p.lab_reports[p.lab_reports.length - 1].sample_date}</div>
                    {p.lab_reports.slice(-3).reverse().filter((r) => r.file_path).map((r) => (
                      <a
                        key={r.id}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.file_path}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`lab-file-${p.bin_number}-${r.id}`}
                        className="mt-1 flex items-center gap-1 text-[11px] text-orange-700 hover:underline"
                      >
                        <FileText className="w-3 h-3" /> {r.file_name || "lab-report.pdf"} <span className="text-gray-400">· {r.sample_date}</span>
                      </a>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setMoveFor(p)} className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border border-gray-300 hover:bg-gray-900 hover:text-white" data-testid={`move-stockpile-${p.bin_number}`}>+ Move</button>
                    <button onClick={() => setLabFor(p)} className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border border-gray-300 hover:bg-gray-900 hover:text-white" data-testid={`lab-stockpile-${p.bin_number}`}>+ Lab</button>
                    <button onClick={() => setUploadFor(p)} className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border border-orange-500 text-orange-700 hover:bg-orange-500 hover:text-white flex items-center gap-1" data-testid={`upload-lab-${p.bin_number}`}>
                      <Upload className="w-3 h-3" /> PDF
                    </button>
                    <button onClick={() => setTransferFor(p)} className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border border-indigo-500 text-indigo-700 hover:bg-indigo-600 hover:text-white flex items-center gap-1" data-testid={`transfer-${p.bin_number}`}>
                      <ArrowRightLeft className="w-3 h-3" /> Transfer
                    </button>
                  </div>
                  <button onClick={() => remove(p.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {newOpen && <NewPileDialog sites={sites} materialTypes={materialTypes} onClose={() => setNewOpen(false)} onSaved={() => { setNewOpen(false); refresh(); }} />}
      {moveFor && <MovementDialog pile={moveFor} onClose={() => setMoveFor(null)} onSaved={() => { setMoveFor(null); refresh(); }} />}
      {labFor && <LabDialog pile={labFor} onClose={() => setLabFor(null)} onSaved={() => { setLabFor(null); refresh(); }} />}
      {uploadFor && <LabUploadDialog pile={uploadFor} onClose={() => setUploadFor(null)} onSaved={() => { setUploadFor(null); refresh(); }} />}
      {transferFor && (
        <TransferDialog
          source={transferFor}
          siblingBins={piles.filter((p) => p.site_id === transferFor.site_id && p.id !== transferFor.id)}
          onClose={() => setTransferFor(null)}
          onSaved={() => { setTransferFor(null); refresh(); }}
        />
      )}
    </div>
  );
}

function LabUploadDialog({ pile, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [sampleDate, setSampleDate] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(pile.classification || "clean");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a PDF"); return; }
    if (file.type !== "application/pdf") { toast.error("Only PDF files are accepted"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(
        `/stockpiles/${pile.id}/lab-reports/upload?sample_date=${encodeURIComponent(sampleDate)}&result=${encodeURIComponent(result)}&notes=${encodeURIComponent(notes)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Lab report uploaded");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Modal title={`Upload Lab PDF · ${pile.name}`} onClose={onClose}>
      <form onSubmit={submit} className="p-5 space-y-4">
        <F label="Sample Date"><input type="date" required value={sampleDate} onChange={(e) => setSampleDate(e.target.value)} className="inp font-mono" data-testid="lab-upload-date-input" /></F>
        <F label="Result (classification tag)">
          <input
            list="stockpile-tags"
            value={result}
            onChange={(e) => setResult(e.target.value)}
            className="inp font-mono"
            data-testid="lab-upload-result-input"
            placeholder="clean / polluted / contaminated / …"
          />
        </F>
        <F label="Notes">
          <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} className="inp" data-testid="lab-upload-notes-input" />
        </F>
        <F label="PDF file">
          <input
            type="file"
            accept="application/pdf"
            required
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="inp"
            data-testid="lab-upload-file-input"
          />
        </F>
        <div className="text-xs text-gray-500">If the result differs from the bin's current classification, the bin will be reclassified automatically.</div>
        <Actions busy={busy} onClose={onClose} label="Upload" testid="submit-lab-upload-button" />
      </form>
      <SharedStyle />
    </Modal>
  );
}

function NewPileDialog({ sites, materialTypes = [], onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", site_id: "", bin_number: "", material_type: "", capacity_tons: 500, classification: "clean" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dropoffSites = sites.filter((s) => s.site_type !== "construction");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/stockpiles", form); toast.success("Bin created"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New Stockpile Bin" onClose={onClose}>
      <form onSubmit={submit} className="p-5 space-y-4">
        <F label="Bin Name"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="stockpile-name-input" /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Bin #"><input required value={form.bin_number} onChange={(e) => set("bin_number", e.target.value)} className="inp font-mono" data-testid="stockpile-binnum-input" /></F>
          <F label="Capacity (t)"><input type="number" step="1" required value={form.capacity_tons} onChange={(e) => set("capacity_tons", parseFloat(e.target.value) || 0)} className="inp" data-testid="stockpile-capacity-input" /></F>
        </div>
        <F label="Site">
          <select required value={form.site_id} onChange={(e) => set("site_id", e.target.value)} className="inp" data-testid="stockpile-site-select">
            <option value="">Select…</option>
            {dropoffSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </F>
        <F label="Material Type">
          <select required value={form.material_type} onChange={(e) => set("material_type", e.target.value)} className="inp" data-testid="stockpile-material-input">
            <option value="">— select —</option>
            {materialTypes.map((mt) => (
              <option key={mt.id} value={mt.code}>{mt.code} · {mt.description}</option>
            ))}
            {form.material_type && !materialTypes.some((mt) => mt.code === form.material_type) && (
              <option value={form.material_type}>{form.material_type} (legacy)</option>
            )}
          </select>
        </F>
        <F label="Classification (custom tag)">
          <input
            list="stockpile-tags"
            value={form.classification}
            onChange={(e) => set("classification", e.target.value)}
            className="inp font-mono"
            data-testid="stockpile-classif-input"
            placeholder="clean / polluted / contaminated / …"
          />
          <datalist id="stockpile-tags">
            {COMMON_TAGS.map((t) => <option key={t} value={t} />)}
          </datalist>
          <div className="text-[11px] text-gray-500 mt-1">Free-form tag. MDC-controlled labels supported (clean, polluted, contaminated, regulated, hazardous, urban fill, …).</div>
        </F>
        <Actions busy={busy} onClose={onClose} label="Create" testid="submit-stockpile-button" />
      </form>
      <SharedStyle />
    </Modal>
  );
}

function MovementDialog({ pile, onClose, onSaved }) {
  const [form, setForm] = useState({ tons: 10, kind: "out", reference: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const tonsNum = parseFloat(form.tons) || 0;
  const cap = parseFloat(pile.capacity_tons) || 0;
  const cur = parseFloat(pile.current_tons) || 0;
  const wouldOverflow = form.kind === "in" && cap > 0 && (cur + tonsNum) > cap;
  const overBy = wouldOverflow ? (cur + tonsNum - cap).toFixed(1) : 0;
  const submit = async (e) => {
    e.preventDefault();
    if (wouldOverflow) { toast.error(`Bin would overflow by ${overBy}t.`); return; }
    setBusy(true);
    try { await api.post(`/stockpiles/${pile.id}/movements`, { ...form, tons: tonsNum }); toast.success("Movement recorded"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Movement · ${pile.name}`} onClose={onClose}>
      <form onSubmit={submit} className="p-5 space-y-4">
        <F label="Direction">
          <select value={form.kind} onChange={(e) => set("kind", e.target.value)} className="inp" data-testid="movement-kind-select">
            <option value="in">In (add)</option>
            <option value="out">Out (remove)</option>
          </select>
        </F>
        <F label="Tons"><input type="number" step="0.1" min="0.01" required value={form.tons} onChange={(e) => set("tons", e.target.value)} className="inp" data-testid="movement-tons-input" /></F>
        <F label="Reference"><input value={form.reference} onChange={(e) => set("reference", e.target.value)} className="inp" data-testid="movement-ref-input" placeholder="BOL, ticket, or note" /></F>
        <div className="text-[11px] text-gray-500 font-mono">
          Bin: {cur.toFixed(1)} / {cap.toFixed(0)}t
          {" · "}
          <span className={wouldOverflow ? "text-red-600 font-bold" : "text-emerald-700"}>
            {wouldOverflow ? `over by ${overBy}t` : `room for ${(cap - cur).toFixed(1)}t`}
          </span>
        </div>
        {wouldOverflow && (
          <div className="text-[11px] text-red-800 bg-red-50 border border-red-200 p-2" data-testid="movement-capacity-warning">
            This movement would push bin over capacity by {overBy}t.
          </div>
        )}
        <Actions busy={busy || wouldOverflow} onClose={onClose} label="Record" testid="submit-movement-button" />
      </form>
      <SharedStyle />
    </Modal>
  );
}

function LabDialog({ pile, onClose, onSaved }) {
  const [form, setForm] = useState({ sample_date: new Date().toISOString().slice(0, 10), result: pile.classification || "clean", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/stockpiles/${pile.id}/lab-reports`, { stockpile_id: pile.id, ...form }); toast.success("Lab report added"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Lab Report · ${pile.name}`} onClose={onClose}>
      <form onSubmit={submit} className="p-5 space-y-4">
        <F label="Sample Date"><input type="date" required value={form.sample_date} onChange={(e) => set("sample_date", e.target.value)} className="inp font-mono" data-testid="lab-date-input" /></F>
        <F label="Result (classification tag)">
          <input
            list="stockpile-tags"
            value={form.result}
            onChange={(e) => set("result", e.target.value)}
            className="inp font-mono"
            data-testid="lab-result-input"
            placeholder="clean / polluted / contaminated / …"
          />
        </F>
        <F label="Notes"><textarea rows="2" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="inp" data-testid="lab-notes-input" /></F>
        <div className="text-xs text-gray-500">If the result differs from the bin's current classification, the bin will be reclassified automatically.</div>
        <Actions busy={busy} onClose={onClose} label="Add Report" testid="submit-lab-button" />
      </form>
      <SharedStyle />
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-md">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold text-xl">{title}</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function F({ label, children }) { return <div><label className="label-caps block mb-1.5">{label}</label>{children}</div>; }
function Actions({ busy, onClose, label, testid }) {
  return (
    <div className="flex gap-3 pt-3">
      <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
      <button type="submit" disabled={busy} data-testid={testid} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold disabled:opacity-40">{busy ? "Saving…" : label}</button>
    </div>
  );
}
function SharedStyle() {
  return <style>{`.inp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; } .inp:focus { outline: none; border-color: #111827; }`}</style>;
}
